import { Accordion, Anchor, Div, H1, H3 } from '@dnb/eufemia';
import { ClientDto } from '@portals/shared/admin/ClientDto';
import { Type } from '@portals/shared/admin/DashboardDto';
import { enUS } from 'date-fns/locale';
import { type JSX, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import useSWR from 'swr';

import BackButton from '@/components/BackButton';
import DataTable, { type Column } from '@/components/DataTable';
import LoadingPage from '@/components/LoadingPage';
import { humanDate } from '@/utils';

const COLUMNS: Column<ClientDto>[] = [
  {
    header: 'Name',
    attribute: 'name',
  },
  {
    header: 'clientId',
    attribute: 'clientId',
    render: ({ clientId }) => {
      return (
        <Anchor element={Link} to={`/clients/${clientId}`}>
          {clientId}
        </Anchor>
      );
    },
  },

  { header: 'Description', attribute: 'description' },
  {
    header: 'Env',
    attribute: 'liveMode',
    render: ({ liveMode }) => {
      return <span>{liveMode ? 'Live' : 'Test'}</span>;
    },
  },
  {
    header: 'appId',
    attribute: 'appId',
    render: ({ appId }) => {
      return (
        <Anchor element={Link} to={`/apps/${appId}`}>
          {appId}
        </Anchor>
      );
    },
  },
  {
    header: 'Created',
    attribute: 'createdAt',
    render: ({ createdAt }) => humanDate(createdAt),
  },
  {
    header: 'Updated',
    attribute: 'updatedAt',
    render: ({ updatedAt }) => humanDate(updatedAt),
  },
];

export default function ClientList(): JSX.Element {
  const { data: clients, isValidating } = useSWR<ClientDto[]>(`/clients`);

  const isLoading = isValidating;

  const chartData = useMemo(() => {
    if (!clients) {
      return {
        type: Type.line,
        labels: [],
        datasets: [],
        title: '',
      };
    }
    let portalClientCount = 0;

    const data = clients
      .sort((a, b) => {
        const dateA = new Date(a.createdAt as string);
        const dateB = new Date(b.createdAt as string);
        return dateA.getTime() - dateB.getTime();
      })
      .map((client) => {
        portalClientCount = portalClientCount + 1;

        return {
          date: client.createdAt,
          portalClientCount,
        };
      });
    return {
      type: Type.line,
      labels: data.map((d) => d.date || ''),
      datasets: [
        {
          label: 'Clients',
          data: data.map((d) => d.portalClientCount),
          borderColor: '#A5E1D2',
          backgroundColor: '#A5E1D280',
          fill: false,
        },
      ],
      title: 'Clients',
    };
  }, [clients]);
  if (isLoading) {
    return <LoadingPage />;
  }
  return (
    <>
      <BackButton to="/">Home</BackButton>

      <H1 top="large">OAuth Clients</H1>
      <Accordion bottom="small" expanded id="single-accordion" remember_state title="Clients graph">
        <H3>Clients</H3>
        <Line
          data={{ datasets: chartData.datasets, labels: chartData.labels }}
          options={{
            responsive: true,
            scales: {
              x: {
                type: 'time' as const,
                adapters: {
                  date: {
                    locale: enUS,
                  },
                },
              },
            },
          }}
        />
      </Accordion>

      {clients && (
        <DataTable
          barContent={<Div />}
          columns={COLUMNS}
          data={clients}
          defaultSortKey="name"
          filterBy={['clientId', 'name', 'description', 'appId', 'liveMode']}
        />
      )}
    </>
  );
}
